import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { siteContent } from '../data/content';

const AutoSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const slides = siteContent.autoSlider || [];
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize after component mounts
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Auto slide functionality
  useEffect(() => {
    if (!isPlaying || slides.length === 0 || !isInitialized) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === slides.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, slides.length, isInitialized]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (slides.length === 0) {
    return null;
  }

  return (
    <section className="relative w-full overflow-hidden py-8 md:py-12 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        <div className="relative max-w-5xl mx-auto">
          {/* Slides Container */}
          <div className="relative h-[350px] md:h-[400px] lg:h-[450px] rounded-xl overflow-hidden shadow-2xl">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                  index === currentIndex
                    ? 'opacity-100 z-10'
                    : 'opacity-0 z-0'
                }`}
                style={{
                  transitionDelay: index === currentIndex ? '0ms' : '0ms'
                }}
              >
                <img
                  src={slide.image}
                  alt={slide.title || "Medical Technology"}
                  className="w-full h-full object-contain"
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchpriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                />
              </div>
            ))}

            {/* Navigation Buttons - Only show if more than 1 slide */}
            {slides.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110 z-20"
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={20} className="text-gray-800" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110 z-20"
                  aria-label="Next slide"
                >
                  <ChevronRight size={20} className="text-gray-800" />
                </button>
              </>
            )}

            {/* Play/Pause Button - Only show if more than 1 slide */}
            {slides.length > 1 && (
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="absolute bottom-4 right-4 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full transition-colors z-20"
                aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
            )}

            {/* Dots Indicator - Only show if more than 1 slide */}
            {slides.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-green-500 w-6'
                        : 'bg-white/60 hover:bg-white'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Progress Bar - Only show if more than 1 slide and auto-playing */}
            {slides.length > 1 && isPlaying && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300/30 z-20">
                <div
                  className="h-full bg-green-500"
                  style={{ 
                    animation: 'progress 5s linear forwards',
                    animationPlayState: isPlaying ? 'running' : 'paused'
                  }}
                />
              </div>
            )}
          </div>

          {/* Slide counter */}
          {slides.length > 1 && (
            <div className="text-center mt-3 text-gray-600 text-sm">
              <span className="font-medium">
                {currentIndex + 1} / {slides.length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* CSS for progress bar animation */}
      <style jsx>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default AutoSlider;